/**
 * Navigation of the v2 shell (design Main.dc.html NAV/CRUMB, docs/research/design2-shell-capture.md
 * §1.2–1.4): screen keys, Russian labels, lucide icons, groups and routes.
 */
import {
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  CalendarClock,
  ChartColumn,
  Clock,
  Files,
  FileText,
  Folder,
  Gauge,
  GanttChart,
  House,
  Landmark,
  Settings,
  Users,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Need } from '../../lib/session';

/** Screens that keep their own month (design `months`). */
export const MONTH_SCREENS = ['overview', 'hours', 'payplan', 'invoices', 'receipts', 'payouts', 'expenses'] as const;
export type MonthScreen = (typeof MONTH_SCREENS)[number];

export type NavKey =
  | 'home'
  | MonthScreen
  | 'roster'
  | 'clients'
  | 'projects'
  | 'entities'
  | 'people'
  | 'finance'
  | 'documents'
  | 'settings';

/** Screens reachable without a nav item. */
export type HiddenScreen = 'gaps';
export type ScreenKey = NavKey | HiddenScreen;

export const NAV_PATHS = {
  home: '/',
  overview: '/overview',
  hours: '/hours',
  payplan: '/payplan',
  invoices: '/invoices',
  receipts: '/receipts',
  payouts: '/payouts',
  expenses: '/expenses',
  roster: '/roster',
  clients: '/clients',
  projects: '/projects',
  entities: '/entities',
  people: '/people',
  finance: '/finance',
  documents: '/documents',
  settings: '/settings',
  gaps: '/gaps',
} as const satisfies Record<ScreenKey, string>;

export type NavGroup = 'Обзор' | 'Работа по месяцам' | 'Команда и проекты' | 'Отчёты';

export interface NavItem {
  readonly key: NavKey;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly group: NavGroup;
  /** Shows the group heading above it (open sidebar only). */
  readonly startsGroup: boolean;
  /** What a person needs to see it (decisions-log W9); Settings shows when any section does. */
  readonly need: Need;
}

const item = (key: NavKey, label: string, icon: LucideIcon, group: NavGroup, need: Need, startsGroup = false): NavItem => ({
  key,
  label,
  icon,
  group,
  startsGroup,
  need,
});

export const NAV: readonly NavItem[] = [
  item('home', 'Главная', House, 'Обзор', 'signed-in'),
  item('overview', 'Обзор', Gauge, 'Работа по месяцам', 'finance.read', true),
  item('hours', 'План проектов', Clock, 'Работа по месяцам', 'hours.read'),
  item('payplan', 'План выплат', CalendarClock, 'Работа по месяцам', 'payplan.read'),
  item('invoices', 'Счета', FileText, 'Работа по месяцам', 'invoices.read'),
  item('receipts', 'Поступления', ArrowDownLeft, 'Работа по месяцам', 'receipts.read'),
  item('payouts', 'Выплаты', ArrowUpRight, 'Работа по месяцам', 'payouts.read'),
  item('expenses', 'Расходы', Wallet, 'Работа по месяцам', 'expenses.read'),
  item('roster', 'Состав', GanttChart, 'Команда и проекты', 'people.read', true),
  item('clients', 'Клиенты', Building2, 'Команда и проекты', 'clients.read'),
  item('projects', 'Проекты', Folder, 'Команда и проекты', 'projects.read'),
  item('entities', 'Юрлица', Landmark, 'Команда и проекты', 'entities.read'),
  item('people', 'Команда', Users, 'Команда и проекты', 'people.read'),
  item('finance', 'Финансы', ChartColumn, 'Отчёты', 'finance.read', true),
  item('documents', 'Документы', Files, 'Отчёты', 'documents.read'),
  item('settings', 'Настройки', Settings, 'Отчёты', 'signed-in'),
];

export const isMonthScreen = (key: ScreenKey): key is MonthScreen =>
  (MONTH_SCREENS as readonly string[]).includes(key);

/** The screen a pathname belongs to (cards belong to their list). */
export function screenForPath(pathname: string): ScreenKey {
  const segment = pathname.split('/')[1] ?? '';
  const match = (Object.keys(NAV_PATHS) as ScreenKey[]).find((key) => NAV_PATHS[key] === `/${segment}`);
  return match && segment ? match : 'home';
}

/** Settings sections (no Appearance — light theme only); `/settings` opens the first. */
export const SETTINGS_SECTIONS = [
  { key: 'templates', path: '/settings/templates', need: 'entities.read' },
  { key: 'business-lines', path: '/settings/business-lines', need: 'settings.write' },
  { key: 'expenses', path: '/settings/expenses', need: 'expenses.read' },
  { key: 'fx', path: '/settings/fx', need: 'fx.read' },
  { key: 'calendars', path: '/settings/calendars', need: 'settings.write' },
  { key: 'backups', path: '/settings/backups', need: 'data.manage' },
  { key: 'data', path: '/settings/data', need: 'data.manage' },
  { key: 'users', path: '/settings/users', need: 'users.manage' },
  { key: 'roles', path: '/settings/roles', need: 'users.manage' },
] as const satisfies ReadonlyArray<{ key: string; path: string; need: Need }>;

export type SettingsSectionKey = (typeof SETTINGS_SECTIONS)[number]['key'];

/** The sections a person may open, in order (Settings hides itself when none is left). */
export function visibleSettingsSections(can: (need: Need) => boolean) {
  return SETTINGS_SECTIONS.filter((section) => can(section.need));
}

/** The menu a person sees (decisions-log W9). */
export function visibleNav(can: (need: Need) => boolean): NavItem[] {
  return NAV.filter((entry) => (entry.key === 'settings' ? visibleSettingsSections(can).length > 0 : can(entry.need)));
}
